/**
 * Source: docs/rules/00-attributes.md and docs/rules/02-creation.md
 *
 * When the rules document changes, change this file to match and bump the
 * ruleset version. The document is authoritative; this is its encoding.
 */

export const ATTRIBUTE_IDS = [
  "tactical",
  "execution",
  "tasking",
  "resolve",
  "reserve",
  "rank",
] as const;

export type AttributeId = (typeof ATTRIBUTE_IDS)[number];

export interface AttributeDef {
  readonly id: AttributeId;
  readonly name: string;
  readonly description: string;
  /** Rank is excluded from the balance rule, both as a subject and as a reference point. */
  readonly exemptFromBalance: boolean;
}

export const ATTRIBUTES: Record<AttributeId, AttributeDef> = {
  tactical: {
    id: "tactical",
    name: "Tactical",
    description:
      "Used in Defense Tests against direct and spinal-mount weapons, and when avoiding collisions and ramming attempts.",
    exemptFromBalance: false,
  },
  execution: {
    id: "execution",
    name: "Execution",
    description: "Attacks and other tests that depend on precision timing.",
    exemptFromBalance: false,
  },
  tasking: {
    id: "tasking",
    name: "Tasking",
    description: "Primarily used for Damage Control.",
    exemptFromBalance: false,
  },
  resolve: {
    id: "resolve",
    name: "Resolve",
    description: "Morale and Stress Reduction Tests.",
    exemptFromBalance: false,
  },
  reserve: {
    id: "reserve",
    name: "Reserve",
    description:
      "Generates Captain's Reserve tokens each round, which are used as power for a ship's systems.",
    exemptFromBalance: false,
  },
  rank: {
    id: "rank",
    name: "Rank",
    description: "Used for Initiative, and has several uses in Campaign games.",
    exemptFromBalance: true,
  },
};

/** Attributes subject to the balance rule, in display order. */
export const BALANCED_ATTRIBUTE_IDS = ATTRIBUTE_IDS.filter(
  (id) => !ATTRIBUTES[id].exemptFromBalance,
);

export const MIN_RATING = 0;

/** The highest rating that can be bought. Rank 6 exists, but only via Distinguished Career. */
export const MAX_PURCHASABLE_RATING = 5;

/**
 * Cumulative cost to reach a rating -- NOT the cost of each step. An attribute
 * at 3 costs 5 in total, not 1 + 3 + 5.
 *
 * Deliberate divergence from printed cards: official Captain cards round Cost
 * for play simplicity, so a card may show a lower Cost than this tool computes
 * for the same ratings. The sample card 62002-01 prints Cost 10 where these
 * ratings total 12. This tool reports the exact figure by design -- do not
 * "fix" it to match a card.
 */
const CUMULATIVE_COST: readonly number[] = [0, 1, 3, 5, 8, 11];

export function costForRating(rating: number): number {
  const cost = CUMULATIVE_COST[rating];
  if (cost === undefined) {
    throw new RangeError(`No cost defined for attribute rating ${rating}`);
  }
  return cost;
}

export function isPurchasableRating(rating: number): boolean {
  return Number.isInteger(rating) && rating >= MIN_RATING && rating <= MAX_PURCHASABLE_RATING;
}

/**
 * No attribute may exceed the lowest attribute by more than this. An attribute
 * at 4 therefore requires every other balanced attribute to be at least 2.
 * Rank is excluded from the comparison entirely.
 */
export const MAX_BALANCE_SPREAD = 2;

/**
 * A captain's Rank may not exceed the average of their other five attributes,
 * rounded to nearest. Rank is exempt from the balance rule but not from this:
 * command authority is capped by demonstrated competence.
 *
 * This bounds the rating a captain may *purchase*. A Quality that grants a Rank
 * -- Distinguished Career's 6 -- is not a purchase and is not capped here.
 */
export function maxRankFor(attributes: Record<string, number>): number {
  const others = BALANCED_ATTRIBUTE_IDS.map((id) => attributes[id] ?? 0);
  const average = others.reduce((sum, rating) => sum + rating, 0) / others.length;
  return Math.min(MAX_PURCHASABLE_RATING, Math.round(average));
}
